import axios from 'axios';
import { API_BASE_URL } from './api';

// Create axios instance for analytics API
const analyticsApiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add a request interceptor to include the auth token in headers
analyticsApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token refresh
analyticsApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 Unauthorized and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          // No refresh token, redirect to login
          window.location.href = '/login';
          return Promise.reject('Session expired');
        }
        
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/accounts/token/refresh/`,
          { refresh: refreshToken }
        );
        
        if (refreshResponse.status === 200) {
          const { access } = refreshResponse.data;
          
          // Update the access token
          localStorage.setItem('accessToken', access);
          
          // Retry the original request with the new token
          originalRequest.headers['Authorization'] = `Bearer ${access}`;
          return analyticsApiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject('Session expired');
      }
    }
    
    return Promise.reject(error);
  }
);

// Analytics API endpoints
const analyticsApi = {
  // Dashboard metrics endpoint
  getDashboardMetrics: () => {
    return analyticsApiClient.get('/analytics/dashboard-metrics/');
  },
  
  // Top sellers endpoints
  getTopSellers: (days = 30, limit = 10) => {
    return analyticsApiClient.get(`/analytics/top-sellers/?days=${days}&limit=${limit}`);
  },
  viewTopSellerDetails: (productId) => {
    return analyticsApiClient.post('/analytics/top-sellers/', { product_id: productId, action: 'view_details' });
  },
  
  // Dead stock endpoints
  getDeadStock: (days = 90, limit = 50) => {
    return analyticsApiClient.get(`/analytics/dead-stock/?days=${days}&limit=${limit}`);
  },
  viewDeadStockList: (days = 90) => {
    return analyticsApiClient.post('/analytics/dead-stock/', { action: 'view_list', days });
  },
  
  // Out of stock endpoints
  viewOutOfStockList: (days = 30) => {
    return analyticsApiClient.post('/analytics/out-of-stock/', { action: 'view_list', days });
  },
  
  // Monthly sales endpoints
  getMonthlySales: (months = 12) => {
    return analyticsApiClient.get(`/analytics/monthly-sales/?months=${months}`);
  },
  viewSalesBreakdown: (period) => {
    return analyticsApiClient.post('/analytics/monthly-sales/', { action: 'view_breakdown', period });
  },
  
  // Yearly sales endpoints
  getYearlySales: (years) => {
    // If years is provided as an array, join it with commas
    const yearsParam = Array.isArray(years) ? years.join(',') : years;
    return analyticsApiClient.get(`/analytics/yearly-sales/${yearsParam ? '?years=' + yearsParam : ''}`);
  },
  
  // Vendor statement analytics endpoints
  getVendorStatementAnalytics: (year, vendorId) => {
    let url = `/analytics/vendor-statements/?year=${year}`;
    if (vendorId) {
      url += `&vendor_id=${vendorId}`;
    }
    return analyticsApiClient.get(url);
  }
};

export default analyticsApi;
